import { useCallback, useEffect, useMemo } from 'react'
import { localStorageKeys } from '@sketch/constants'
import { useUserProfile } from '@sketch/modules-common'
import * as localStorage from '@sketch/utils/src/localStorage/localStorage'

/**
 * Per-user, per-workspace tracking of recently opened shares.
 *
 * Storage is keyed first by userId then by workspaceId, with each entry
 * storing only the share id plus `firstOpened` / `lastOpened` timestamps — NO
 * share metadata snapshot. Share metadata (name, thumbnail, …) is fetched
 * on-demand from GraphQL by `useRecentlyOpenedSharesQuery`.
 *
 * Scoping by userId lets the list survive sign-out (it's no longer in
 * `authorizationKeys`) without leaking one user's recent shares to another user
 * signing in on the same device.
 */

export const RECENTLY_OPENED_SHARES_LIMIT = 20

// A workspace's list is dropped if it hasn't been updated within this window.
export const RECENTLY_OPENED_STALE_MS = 365 * 24 * 60 * 60 * 1000 // 1 year

export type RecentShareEntry = {
  shareId: string
  firstOpened: number // ms epoch
  lastOpened: number // ms epoch
}

export type WorkspaceRecentShares = {
  lastUpdated: number // ms epoch — bumped whenever this workspace's list changes
  shares: RecentShareEntry[]
}

type RecentlyOpenedStore = Record<
  string /* userId */,
  Record<string /* workspaceId */, WorkspaceRecentShares>
>

function readStore(): RecentlyOpenedStore {
  return (
    localStorage.getParsedItem<RecentlyOpenedStore>(
      localStorageKeys.recentlyOpenedShares
    ) ?? {}
  )
}

function writeStore(store: RecentlyOpenedStore): void {
  localStorage.setStringifiedItem(localStorageKeys.recentlyOpenedShares, store)
}

function upsertEntry(
  store: RecentlyOpenedStore,
  userId: string,
  workspaceId: string,
  shareId: string,
  now: number
): RecentlyOpenedStore {
  const userStore = store[userId] ?? {}
  const workspaceEntries = userStore[workspaceId]?.shares ?? []
  const existing = workspaceEntries.find(e => e.shareId === shareId)
  const updated: RecentShareEntry = {
    shareId,
    firstOpened: existing?.firstOpened ?? now,
    lastOpened: now,
  }
  const withoutCurrent = workspaceEntries.filter(e => e.shareId !== shareId)
  // Insert in head and only keep last X items
  const newWorkspaceEntries = [updated, ...withoutCurrent].slice(
    0,
    RECENTLY_OPENED_SHARES_LIMIT
  )

  return {
    ...store,
    [userId]: {
      ...userStore,
      [workspaceId]: { lastUpdated: now, shares: newWorkspaceEntries },
    },
  }
}

function removeEntries(
  store: RecentlyOpenedStore,
  userId: string,
  workspaceId: string,
  shareIds: string[],
  now: number
): RecentlyOpenedStore {
  const userStore = store[userId] ?? {}
  const workspaceEntries = userStore[workspaceId]?.shares ?? []
  const toRemove = new Set(shareIds)
  const newWorkspaceEntries = workspaceEntries.filter(
    e => !toRemove.has(e.shareId)
  )

  const newUserStore = { ...userStore }
  if (newWorkspaceEntries.length === 0) {
    delete newUserStore[workspaceId]
  } else {
    newUserStore[workspaceId] = {
      lastUpdated: now,
      shares: newWorkspaceEntries,
    }
  }

  const newStore = { ...store }
  if (Object.keys(newUserStore).length === 0) {
    delete newStore[userId]
  } else {
    newStore[userId] = newUserStore
  }
  return newStore
}

/**
 * Returns a function to register the share in recently opened shares — calling it upserts an entry for the given
 * (workspaceId, shareId), bumping `lastOpened` while preserving `firstOpened`.
 * Dedupes, sorts most-recent-first, and caps at RECENTLY_OPENED_SHARES_LIMIT.
 */
export function useRegisterRecentlyOpenedShare() {
  const userId = useUserProfile().data?.me.identifier
  return useCallback(
    (shareId: string, workspaceId: string) => {
      if (!shareId || !workspaceId || !userId) return
      const store = readStore()
      writeStore(upsertEntry(store, userId, workspaceId, shareId, Date.now()))
    },
    [userId]
  )
}

/**
 * Returns a remover function — used by `useRecentlyOpenedSharesQuery` to prune
 * entries whose share came back `null` (deleted or no longer accessible).
 */
export function useRemoveRecentlyOpenedShares() {
  const userId = useUserProfile().data?.me.identifier
  return useCallback(
    (shareIds: string[], workspaceId: string) => {
      if (!shareIds.length || !workspaceId || !userId) return
      const store = readStore()
      writeStore(
        removeEntries(store, userId, workspaceId, shareIds, Date.now())
      )
    },
    [userId]
  )
}

/**
 * Returns a copy of the store with stale or empty data removed. For each user,
 * keeps only the workspaces that still have shares and whose `lastUpdated` falls
 * within `RECENTLY_OPENED_STALE_MS` of now; any user left with no surviving
 * workspaces is dropped entirely so empty user entries don't accumulate. Does
 * not mutate `store`.
 */
function pruneStaleWorkspaces(store: RecentlyOpenedStore): RecentlyOpenedStore {
  const now = Date.now()
  const newStore: RecentlyOpenedStore = {}
  for (const [userId, userStore] of Object.entries(store)) {
    const freshWorkspaces: Record<string, WorkspaceRecentShares> = {}
    for (const [workspaceId, workspace] of Object.entries(userStore)) {
      if (
        workspace.shares.length > 0 &&
        now - workspace.lastUpdated <= RECENTLY_OPENED_STALE_MS
      ) {
        freshWorkspaces[workspaceId] = workspace
      }
    }
    // Drop the user entirely once they have no fresh workspaces left.
    if (Object.keys(freshWorkspaces).length > 0) {
      newStore[userId] = freshWorkspaces
    }
  }
  return newStore
}

/**
 * Removes every workspace whose list hasn't been updated within
 * `RECENTLY_OPENED_STALE_MS`, and drops any user left with no workspaces.
 * Exported for testing.
 */
export function cleanupRecentlyOpenedShares(): void {
  writeStore(pruneStaleWorkspaces(readStore()))
}

/**
 * Runs `cleanupRecentlyOpenedShares` once on mount (when `enabled`). Intended to
 * be mounted near the app root so stale workspaces are pruned on every app
 * load.
 */
export function useCleanupRecentlyOpenedSharesStore(enabled = true): void {
  useEffect(() => {
    if (enabled) cleanupRecentlyOpenedShares()
  }, [enabled])
}

/**
 * Returns the ordered list of recently-opened share IDs for the current user
 * in the given workspace (most-recent first). Recomputes only when the user or
 * workspace changes; the view doesn't need to react to writes from other
 * tabs/pages.
 */
export function useRecentlyOpenedShareIds(workspaceId: string): string[] {
  const userId = useUserProfile().data?.me.identifier
  return useMemo<string[]>(() => {
    if (!userId) return []
    const store = readStore()
    const list = store[userId]?.[workspaceId]?.shares ?? []
    return list.map(e => e.shareId)
  }, [userId, workspaceId])
}
